import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Copy, Ellipsis, Plus } from 'lucide-react'
import { rolesQuery } from '@/api/queries'
import type { Role } from '@/api/types'
import { useCompanyCaller } from '@/app/company'
import { NothingForRole } from '@/app/nothing-for-role'
import { canBuildRoles, companySections, roleVerdict, type Caller } from '@/domain/abilities'
import { errorText } from '@/domain/errors'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { RowMenu, RowMenuItem } from '@/ui/overlays'
import { Page, PageHeader } from '@/ui/page'
import { SectionHeader } from '@/ui/section-header'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/roles/')({
  component: Roles,
})

/// Ролі: the seven system templates (read-only, «Копіювати») and the
/// company's own roles. Building is for `staff = manage` (R5); everyone with
/// `staff ≥ view` can read what each role may do.
function Roles() {
  const { orgId } = Route.useParams()
  const { caller } = useCompanyCaller(orgId)
  const allowed = companySections(caller).includes('roles')
  // Not even asked for without `staff ≥ view` — the server would refuse it.
  const roles = useQuery({ ...rolesQuery(orgId), enabled: allowed })
  const navigate = useNavigate()

  if (!allowed) return <NothingForRole />

  const build = canBuildRoles(caller)
  const copyRole = (role: Role) =>
    void navigate({ to: '/c/$orgId/roles/new', params: { orgId }, search: { from: role.id } })

  const tableProps = { orgId, caller, onCopy: copyRole }
  const system = roles.data?.filter((r) => r.system) ?? []
  const custom = roles.data?.filter((r) => !r.system) ?? []

  return (
    <Page wide>
      <PageHeader
        title={t.roles.title}
        subtitle={t.roles.subtitle}
        actions={
          build ? (
            <Button
              variant="primary"
              icon={<Plus className="size-[18px]" strokeWidth={2.5} />}
              onClick={() => void navigate({ to: '/c/$orgId/roles/new', params: { orgId }, search: {} })}
            >
              {t.roles.create}
            </Button>
          ) : undefined
        }
      />

      {roles.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : roles.isError ? (
        <InlineBanner
          kind="error"
          message={errorText(roles.error)}
          action={{ label: t.app.retry, onClick: () => void roles.refetch() }}
        />
      ) : (
        <div className="flex flex-col gap-10">
          <section className="flex flex-col gap-2" aria-labelledby="roles-custom">
            <SectionHeader id="roles-custom" title={t.roles.custom} count={custom.length || undefined} />
            {custom.length === 0 ? (
              <InlineBanner kind="empty" title={t.roles.noCustom} message={t.roles.noCustomMessage} />
            ) : (
              <RoleTable label={t.roles.custom} roles={custom} {...tableProps} />
            )}
          </section>
          <section className="flex flex-col gap-2" aria-labelledby="roles-system">
            <SectionHeader id="roles-system" title={t.roles.system} count={system.length} />
            <RoleTable label={t.roles.system} roles={system} {...tableProps} />
          </section>
        </div>
      )}
    </Page>
  )
}

/// The same columns in both groups, so «Власні» and «Системні» line up
/// into one reading: name, whom it reaches, how many people hold it.
const columns: readonly DataColumn<Role>[] = [
  {
    key: 'name',
    header: t.roles.columns.name,
    width: 'minmax(0,2fr)',
    cell: (role) => (
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate">{role.name}</span>
        {role.createdByActor === 'eatlete' ? (
          <span title={t.roles.byEatlete} className="shrink-0 rounded-full px-1.5 py-px text-footnote font-medium text-text-2 tint-active">
            {t.roles.eatlete}
          </span>
        ) : null}
      </span>
    ),
  },
  { key: 'scope', header: t.roles.columns.scope, width: 'minmax(0,1fr)', cell: (role) => t.scopes[role.scope] },
  {
    key: 'people',
    header: t.roles.columns.people,
    width: '88px',
    numeric: true,
    mobile: 'trailing',
    // A bare number under «Люди» — «1 людина» down a column is the same
    // word seven times.
    cell: (role) => (role.members === 0 ? <span className="text-muted">{t.roles.nobody}</span> : String(role.members)),
  },
]

const RoleTable = ({
  label,
  roles,
  orgId,
  caller,
  onCopy,
}: {
  label: string
  roles: readonly Role[]
  orgId: string
  caller: Caller
  onCopy: (role: Role) => void
}) => (
  <DataList
    label={label}
    columns={columns}
    rows={roles}
    rowKey={(role) => role.id}
    rowTarget={(role) => ({ to: '/c/$orgId/roles/$roleId', params: { orgId, roleId: role.id } })}
    rowActions={(role) =>
      roleVerdict(caller, role).canCopy ? (
        <RowMenu label={`${t.app.more}: ${role.name}`} trigger={<Ellipsis className="size-[18px]" />}>
          <RowMenuItem icon={<Copy className="size-4" />} onSelect={() => onCopy(role)}>
            {t.roles.copy}
          </RowMenuItem>
        </RowMenu>
      ) : null
    }
  />
)
